/**
 * Stage 4: Media - Experience Creation Wizard
 *
 * Collects photos and media for the experience:
 * - Cover photo upload (required)
 * - Gallery photo multi-upload (3-10 recommended)
 * - Photo reordering (with API sync)
 * - Photo captions (optional)
 * - Set gallery photo as cover
 * - Delete via API (removes S3 objects)
 * - Load existing photos from API on mount
 *
 * Epic: MVP 1.27 - Experience Creation Wizard
 * Stories: SCRUM-204 to SCRUM-207, SCRUM-783 (AssetService integration)
 * Location: apps/web/src/components/experiences/creation/stages/Stage4Media.jsx
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import FieldWrapper from '../../../common/forms/FieldWrapper';
import FormActions from '../../../common/forms/FormActions';
import {
  uploadAsset,
  getAssetUrl,
  getAssetsByEntity,
  deleteAsset,
  reorderAssets,
  setPrimaryAsset,
} from '../../../../services/assetService';

/**
 * Stage4Media Component
 *
 * @param {Object} props
 * @param {Object} props.formData - Current wizard form data
 * @param {Function} props.updateField - Update single field
 * @param {Function} props.updateFormData - Update multiple fields
 * @param {Function} props.onNext - Navigate to next stage
 * @param {Function} props.onBack - Navigate to previous stage
 * @param {Object} props.validation - Validation errors for current stage
 * @param {boolean} props.isValid - Whether current stage is valid
 * @param {string} [props.experienceId] - Experience UUID (for existing experiences)
 */
export default function Stage4Media({
  formData,
  updateField,
  updateFormData: _updateFormData,
  onNext,
  onBack,
  validation,
  isValid,
  experienceId
}) {
  // Extract current values
  const {
    coverPhoto = null,
    galleryPhotos = []
  } = formData;

  // Local state
  const [error, setError] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loadingAssets, setLoadingAssets] = useState(false);

  // Store failed file info for retry (AC-05)
  const failedUploadRef = useRef(null);

  // Generate a provisional entity ID for new experiences (no experienceId yet)
  const [provisionalId] = useState(() => {
    if (experienceId) {
      return experienceId;
    }
    // Use existing provisional ID from formData if available
    if (formData._provisionalEntityId) {
      return formData._provisionalEntityId;
    }
    // Generate a new UUID
    const id = crypto.randomUUID();
    // Store it so it persists across re-renders
    _updateFormData({ _provisionalEntityId: id });
    return id;
  });

  const entityId = experienceId || provisionalId;

  /**
   * AC-11: Load existing photos from assets API on mount.
   * When navigating back to Stage4 from Stage5, photos reload from API.
   */
  useEffect(() => {
    let cancelled = false;

    async function loadExistingAssets() {
      if (!entityId) {
        return;
      }

      try {
        setLoadingAssets(true);
        const assets = await getAssetsByEntity('experience', entityId);

        if (cancelled || !assets || assets.length === 0) {
          return;
        }

        // Separate cover from gallery
        let loadedCover = null;
        const loadedGallery = [];

        for (const asset of assets) {
          const url = getAssetUrl(asset, asset.asset_purpose === 'cover' ? 'large' : 'medium')
            || getAssetUrl(asset, 'original');

          const photoObj = {
            url,
            assetId: asset.id,
            filename: asset.original_filename,
            size: asset.file_size_bytes,
            type: asset.mime_type,
            caption: asset.caption || ''
          };

          if (asset.asset_purpose === 'cover' || asset.is_primary) {
            loadedCover = photoObj;
          } else {
            loadedGallery.push(photoObj);
          }
        }

        // Only update if we loaded data and current formData doesn't already have it
        if (loadedCover && !formData.coverPhoto?.assetId) {
          updateField('coverPhoto', loadedCover);
        }
        if (loadedGallery.length > 0 && (!formData.galleryPhotos || formData.galleryPhotos.length === 0)) {
          updateField('galleryPhotos', loadedGallery);
        }
      } catch {
        // Silently fail — photos may not exist yet for new experiences
      } finally {
        if (!cancelled) {
          setLoadingAssets(false);
        }
      }
    }

    loadExistingAssets();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  /**
   * File validation
   */
  const validateFile = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

    if (file.size > maxSize) {
      return { valid: false, error: 'File size must be less than 5MB' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'File must be JPG, PNG, or WebP' };
    }

    return { valid: true };
  };

  /**
   * Upload file to AssetService API.
   * Returns the stored asset with CDN URLs for all variants.
   * AC-04: Simulates upload progress during the request.
   * @param {File} file - File to upload
   * @param {string} assetPurpose - 'cover' or 'gallery'
   * @returns {Promise<Object>} Stored asset with variants
   */
  const uploadFile = useCallback(async (file, assetPurpose = 'cover') => {
    // AC-04: Start progress simulation
    setUploadProgress(0);
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        // Asymptotically approach 90% during upload
        if (prev < 90) {
          return prev + (90 - prev) * 0.1;
        }
        return prev;
      });
    }, 200);

    try {
      const asset = await uploadAsset(file, {
        entityType: 'experience',
        entityId,
        category: 'image',
        assetPurpose,
      });

      // Upload complete
      setUploadProgress(100);
      setTimeout(() => setUploadProgress(0), 300);

      return asset;
    } finally {
      clearInterval(progressInterval);
    }
  }, [entityId]);

  /**
   * Dismiss error banner (AC-05)
   */
  const dismissError = useCallback(() => {
    setError(null);
    failedUploadRef.current = null;
  }, []);

  /**
   * Retry failed upload (AC-05)
   */
  const retryUpload = useCallback(async () => {
    const failedInfo = failedUploadRef.current;
    if (!failedInfo) {
      return;
    }

    setError(null);

    if (failedInfo.type === 'cover') {
      // Re-trigger cover upload
      try {
        setUploadingCover(true);
        const asset = await uploadFile(failedInfo.file, 'cover');
        const url = getAssetUrl(asset, 'large') || getAssetUrl(asset, 'original');
        updateField('coverPhoto', {
          url,
          assetId: asset.id,
          filename: failedInfo.file.name,
          size: failedInfo.file.size,
          type: failedInfo.file.type
        });
        failedUploadRef.current = null;
      } catch {
        setError('Failed to upload cover photo. Please try again.');
        // Keep failedUploadRef for another retry
      } finally {
        setUploadingCover(false);
      }
    } else if (failedInfo.type === 'gallery') {
      // Re-trigger gallery upload
      try {
        setUploadingGallery(true);
        const uploadPromises = failedInfo.files.map(async (file) => {
          const asset = await uploadFile(file, 'gallery');
          const url = getAssetUrl(asset, 'medium') || getAssetUrl(asset, 'original');
          return {
            url,
            assetId: asset.id,
            filename: file.name,
            size: file.size,
            type: file.type,
            caption: ''
          };
        });

        const uploadedPhotos = await Promise.all(uploadPromises);
        updateField('galleryPhotos', [...galleryPhotos, ...uploadedPhotos]);
        failedUploadRef.current = null;
      } catch {
        setError('Failed to upload gallery photos. Please try again.');
      } finally {
        setUploadingGallery(false);
      }
    }
  }, [uploadFile, updateField, galleryPhotos]);

  /**
   * Handle cover photo upload - SCRUM-204, SCRUM-783
   */
  const handleCoverPhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {return;}

    // Validate file
    const fileValidation = validateFile(file);
    if (!fileValidation.valid) {
      setError(fileValidation.error);
      failedUploadRef.current = null;
      return;
    }

    try {
      setUploadingCover(true);
      setError(null);

      // AC-12: If replacing existing cover, delete old asset from API
      if (coverPhoto?.assetId) {
        try {
          await deleteAsset(coverPhoto.assetId);
        } catch {
          // Best-effort cleanup — continue with new upload
        }
      }

      // Upload to AssetService
      const asset = await uploadFile(file, 'cover');

      // Get best display URL from variants
      const url = getAssetUrl(asset, 'large') || getAssetUrl(asset, 'original');

      // Update form data with CDN URL
      updateField('coverPhoto', {
        url,
        assetId: asset.id,
        filename: file.name,
        size: file.size,
        type: file.type
      });
      failedUploadRef.current = null;
    } catch {
      setError('Failed to upload cover photo. Please try again.');
      failedUploadRef.current = { type: 'cover', file };
    } finally {
      setUploadingCover(false);
    }
  };

  /**
   * Handle gallery photo upload - SCRUM-205, SCRUM-783
   */
  const handleGalleryPhotoUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {return;}

    // Check max photos limit
    if (galleryPhotos.length + files.length > 10) {
      setError('Maximum 10 gallery photos allowed');
      failedUploadRef.current = null;
      return;
    }

    // Validate all files
    for (const file of files) {
      const fileValidation = validateFile(file);
      if (!fileValidation.valid) {
        setError(`${file.name}: ${fileValidation.error}`);
        failedUploadRef.current = null;
        return;
      }
    }

    try {
      setUploadingGallery(true);
      setError(null);

      // Upload files concurrently
      const uploadPromises = files.map(async (file) => {
        const asset = await uploadFile(file, 'gallery');
        const url = getAssetUrl(asset, 'medium') || getAssetUrl(asset, 'original');
        return {
          url,
          assetId: asset.id,
          filename: file.name,
          size: file.size,
          type: file.type,
          caption: ''
        };
      });

      const uploadedPhotos = await Promise.all(uploadPromises);

      // Add to gallery
      updateField('galleryPhotos', [...galleryPhotos, ...uploadedPhotos]);
      failedUploadRef.current = null;
    } catch {
      setError('Failed to upload gallery photos. Please try again.');
      failedUploadRef.current = { type: 'gallery', files };
    } finally {
      setUploadingGallery(false);
    }
  };

  /**
   * Handle drag events for gallery photos
   */
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  /**
   * Handle drop event for gallery photos - SCRUM-783
   */
  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) {return;}

    // Check max photos limit
    if (galleryPhotos.length + files.length > 10) {
      setError('Maximum 10 gallery photos allowed');
      failedUploadRef.current = null;
      return;
    }

    // Filter only images
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      setError('Please drop image files only');
      failedUploadRef.current = null;
      return;
    }

    // Validate all files
    for (const file of imageFiles) {
      const fileValidation = validateFile(file);
      if (!fileValidation.valid) {
        setError(`${file.name}: ${fileValidation.error}`);
        failedUploadRef.current = null;
        return;
      }
    }

    try {
      setUploadingGallery(true);
      setError(null);

      // Upload files via AssetService
      const uploadPromises = imageFiles.map(async (file) => {
        const asset = await uploadFile(file, 'gallery');
        const url = getAssetUrl(asset, 'medium') || getAssetUrl(asset, 'original');
        return {
          url,
          assetId: asset.id,
          filename: file.name,
          size: file.size,
          type: file.type,
          caption: ''
        };
      });

      const uploadedPhotos = await Promise.all(uploadPromises);

      // Add to gallery
      updateField('galleryPhotos', [...galleryPhotos, ...uploadedPhotos]);
      failedUploadRef.current = null;
    } catch {
      setError('Failed to upload gallery photos. Please try again.');
      failedUploadRef.current = { type: 'gallery', files: imageFiles };
    } finally {
      setUploadingGallery(false);
    }
  }, [galleryPhotos, updateField, uploadFile]);

  /**
   * Remove cover photo — AC-12: calls DELETE API to remove S3 objects
   */
  const handleRemoveCoverPhoto = async () => {
    if (coverPhoto?.assetId) {
      try {
        await deleteAsset(coverPhoto.assetId);
      } catch {
        // Best-effort — still remove from UI
      }
    }
    updateField('coverPhoto', null);
  };

  /**
   * Remove gallery photo — AC-12: calls DELETE API to remove S3 objects
   */
  const handleRemoveGalleryPhoto = async (index) => {
    // eslint-disable-next-line security/detect-object-injection -- safe: index is controlled array index from UI interaction
    const photo = galleryPhotos[index];
    if (photo?.assetId) {
      try {
        await deleteAsset(photo.assetId);
      } catch {
        // Best-effort — still remove from UI
      }
    }

    const updated = galleryPhotos.filter((_, i) => i !== index);
    updateField('galleryPhotos', updated);

    // AC-09: Sync reorder after removal
    const remainingIds = updated.filter(p => p.assetId).map(p => p.assetId);
    if (remainingIds.length > 0) {
      try {
        await reorderAssets('experience', entityId, remainingIds);
      } catch {
        // Best-effort
      }
    }
  };

  /**
   * Move gallery photo up — AC-09: calls PUT reorder endpoint
   */
  const handleMovePhotoUp = async (index) => {
    if (index === 0) {return;}

    const updated = [...galleryPhotos];
    // eslint-disable-next-line security/detect-object-injection -- safe: index is controlled array index from UI interaction
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updateField('galleryPhotos', updated);

    // AC-09: Sync new order to API
    const assetIds = updated.filter(p => p.assetId).map(p => p.assetId);
    if (assetIds.length > 0) {
      try {
        await reorderAssets('experience', entityId, assetIds);
      } catch {
        // Best-effort — local order is already updated
      }
    }
  };

  /**
   * Move gallery photo down — AC-09: calls PUT reorder endpoint
   */
  const handleMovePhotoDown = async (index) => {
    if (index === galleryPhotos.length - 1) {return;}

    const updated = [...galleryPhotos];
    // eslint-disable-next-line security/detect-object-injection -- safe: index is controlled array index from UI interaction
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updateField('galleryPhotos', updated);

    // AC-09: Sync new order to API
    const assetIds = updated.filter(p => p.assetId).map(p => p.assetId);
    if (assetIds.length > 0) {
      try {
        await reorderAssets('experience', entityId, assetIds);
      } catch {
        // Best-effort — local order is already updated
      }
    }
  };

  /**
   * AC-10: Set a gallery photo as the cover photo
   */
  const handleSetAsCover = async (index) => {
    // eslint-disable-next-line security/detect-object-injection -- safe: index is controlled array index from UI interaction
    const photo = galleryPhotos[index];
    if (!photo) {return;}

    // Set as primary in API
    if (photo.assetId) {
      try {
        await setPrimaryAsset('experience', entityId, photo.assetId);
      } catch {
        // Best-effort — still update locally
      }
    }

    // Get the large variant URL for cover display
    const coverUrl = photo.url; // Already has a URL

    // Remove from gallery and set as cover
    const updatedGallery = galleryPhotos.filter((_, i) => i !== index);

    // If there's an existing cover, move it back to gallery
    if (coverPhoto) {
      updatedGallery.unshift(coverPhoto);
    }

    updateField('coverPhoto', {
      url: coverUrl,
      assetId: photo.assetId,
      filename: photo.filename,
      size: photo.size,
      type: photo.type
    });
    updateField('galleryPhotos', updatedGallery);
  };

  /**
   * Update photo caption - SCRUM-207
   */
  const handleCaptionChange = (index, caption) => {
    const updated = [...galleryPhotos];
    // eslint-disable-next-line security/detect-object-injection -- safe: index is controlled array index from UI interaction
    updated[index] = { ...updated[index], caption };
    updateField('galleryPhotos', updated);
  };

  const isUploading = uploadingCover || uploadingGallery;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Photos & Media</h2>

      {/* AC-04: Upload Progress Bar */}
      {isUploading && uploadProgress > 0 && (
        <div className="mb-4" data-testid="upload-progress">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-emerald-700">Uploading...</span>
            <span className="text-sm text-gray-500">{Math.round(uploadProgress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-emerald-600 h-2 rounded-full transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Loading indicator for asset fetch */}
      {loadingAssets && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm" data-testid="loading-assets">
          Loading existing photos...
        </div>
      )}

      {/* AC-05: Error Banner with Dismiss and Retry */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between" data-testid="error-banner">
          <span className="text-red-700 text-sm">{error}</span>
          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
            {failedUploadRef.current && (
              <button
                type="button"
                onClick={retryUpload}
                className="text-sm font-medium text-red-700 hover:text-red-800 underline"
                data-testid="retry-button"
              >
                Retry
              </button>
            )}
            <button
              type="button"
              onClick={dismissError}
              className="text-red-400 hover:text-red-600"
              aria-label="Dismiss error"
              data-testid="dismiss-error"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Cover Photo Upload - SCRUM-204 */}
        <div>
          <FieldWrapper
            label="Cover Photo"
            htmlFor="coverPhoto"
            required={true}
            error={validation?.errors?.coverPhoto}
            helperText="This is the main photo guests will see first. Choose a high-quality image that represents your experience."
          >
            {!coverPhoto ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-emerald-500 transition-colors">
                <input
                  id="coverPhoto"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleCoverPhotoUpload}
                  disabled={uploadingCover}
                  className="hidden"
                />
                <label
                  htmlFor="coverPhoto"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700 mb-1">
                    {uploadingCover ? 'Uploading...' : 'Click to upload cover photo'}
                  </span>
                  <span className="text-xs text-gray-500">
                    JPG, PNG, or WebP up to 5MB
                  </span>
                </label>
              </div>
            ) : (
              <div className="relative group">
                <img
                  src={coverPhoto.url}
                  alt="Cover"
                  className="w-full h-64 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={handleRemoveCoverPhoto}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                  >
                    Remove
                  </button>
                  <label
                    htmlFor="coverPhoto"
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium cursor-pointer"
                  >
                    Replace
                  </label>
                  <input
                    id="coverPhoto"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleCoverPhotoUpload}
                    disabled={uploadingCover}
                    className="hidden"
                  />
                </div>
              </div>
            )}
          </FieldWrapper>
        </div>

        {/* Gallery Photos Upload - SCRUM-205 */}
        <div className="border-t border-gray-200 pt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gallery Photos
            <span className="text-gray-500 font-normal ml-2">(3-10 photos recommended)</span>
          </label>
          <p className="text-sm text-gray-600 mb-4">
            Add more photos to showcase your experience from different angles and moments.
          </p>

          {/* Drag and Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors mb-4 ${
              dragActive
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              id="galleryPhotos"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              multiple
              onChange={handleGalleryPhotoUpload}
              disabled={uploadingGallery || galleryPhotos.length >= 10}
              className="hidden"
            />
            <label
              htmlFor="galleryPhotos"
              className="cursor-pointer flex flex-col items-center"
            >
              <svg className="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm font-medium text-gray-700 mb-1">
                {uploadingGallery ? 'Uploading...' : 'Drag and drop photos here, or click to browse'}
              </span>
              <span className="text-xs text-gray-500">
                JPG, PNG, or WebP up to 5MB each (max 10 photos)
              </span>
            </label>
          </div>

          {/* Gallery Photos Grid - SCRUM-206 & SCRUM-207 */}
          {galleryPhotos.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {galleryPhotos.map((photo, index) => (
                <div key={photo.assetId || index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  {/* Photo Preview */}
                  <div className="relative group h-48">
                    <img
                      src={photo.url}
                      alt={photo.caption || `Gallery photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {/* Action Buttons */}
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {/* AC-10: Set as Cover */}
                      <button
                        type="button"
                        onClick={() => handleSetAsCover(index)}
                        className="p-1 bg-emerald-600/90 rounded hover:bg-emerald-600 text-white"
                        title="Set as cover"
                        data-testid={`set-cover-${index}`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </button>
                      {/* Reorder Buttons - SCRUM-206 */}
                      <button
                        type="button"
                        onClick={() => handleMovePhotoUp(index)}
                        disabled={index === 0}
                        className="p-1 bg-white/90 rounded hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMovePhotoDown(index)}
                        disabled={index === galleryPhotos.length - 1}
                        className="p-1 bg-white/90 rounded hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveGalleryPhoto(index)}
                        className="p-1 bg-red-600/90 rounded hover:bg-red-600"
                        title="Remove"
                      >
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    {/* Photo Number Badge */}
                    <div className="absolute top-2 left-2 bg-black/70 text-white text-xs font-semibold px-2 py-1 rounded">
                      {index + 1}
                    </div>
                  </div>

                  {/* Caption Input - SCRUM-207 */}
                  <div className="p-3">
                    <input
                      type="text"
                      value={photo.caption}
                      onChange={(e) => handleCaptionChange(index, e.target.value)}
                      placeholder="Add a caption (optional)"
                      maxLength={200}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {photo.caption.length}/200 characters
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Photo Count and Warning */}
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {galleryPhotos.length} of 10 photos
              {galleryPhotos.length < 3 && (
                <span className="text-amber-600 ml-2">
                  (Add {3 - galleryPhotos.length} more for better visibility)
                </span>
              )}
            </p>
            {galleryPhotos.length >= 10 && (
              <p className="text-sm text-amber-600">Maximum photos reached</p>
            )}
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <FormActions
        onCancel={onBack}
        onSubmit={onNext}
        hasUnsavedChanges={true}
        isSaving={false}
        cancelLabel="Back"
        submitLabel="Continue to Review"
        showSaveDraft={false}
        disableSubmit={!isValid}
        submitVariant="success"
        layout="split"
      />
    </div>
  );
}
